import { Children, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

interface ISecaoGradeProps {
  titulo: string;
  subtitulo?: string;
  icone?: React.ReactNode;
  // Controles à direita do título (filtros, atualizar...), antes das setas.
  acoes?: React.ReactNode;
  // Largura mínima de cada card — define quantos cabem na linha.
  larguraMinimaCard: number;
  children: React.ReactNode;
}

const ESPACO_ENTRE_CARDS = 12; // px — mesmo valor do gap={1.5} abaixo

// Seção da tela inicial com UMA linha de cards: mostra quantos couberem na
// largura atual e as setas trocam pro grupo seguinte/anterior — sem barra de
// rolagem lateral. A quantidade por linha se ajusta sozinha ao redimensionar.
export const SecaoGrade: React.FC<ISecaoGradeProps> = ({ titulo, subtitulo, icone, acoes, larguraMinimaCard, children }) => {
  const theme = useTheme();
  const linhaRef = useRef<HTMLDivElement>(null);
  const [porPagina, setPorPagina] = useState(1);
  const [pagina, setPagina] = useState(0);

  // useLayoutEffect: mede antes da primeira pintura, senão a linha aparece
  // por um instante com 1 card só.
  useLayoutEffect(() => {
    const linha = linhaRef.current;
    if (!linha) return;
    const medir = () => {
      const largura = linha.clientWidth;
      setPorPagina(Math.max(1, Math.floor((largura + ESPACO_ENTRE_CARDS) / (larguraMinimaCard + ESPACO_ENTRE_CARDS))));
    };
    medir();
    const observer = new ResizeObserver(medir);
    observer.observe(linha);
    return () => observer.disconnect();
  }, [larguraMinimaCard]);

  const itens = Children.toArray(children);
  const totalPaginas = Math.max(1, Math.ceil(itens.length / porPagina));
  // Ao mudar o filtro ou a largura, a página atual pode deixar de existir.
  const paginaAtual = Math.min(pagina, totalPaginas - 1);
  useEffect(() => {
    if (pagina !== paginaAtual) setPagina(paginaAtual);
  }, [pagina, paginaAtual]);

  const visiveis = itens.slice(paginaAtual * porPagina, (paginaAtual + 1) * porPagina);
  const botaoSx = { border: `1px solid ${theme.torq.border.default}` };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.25} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          {icone && <Box display="flex" color="primary.main">{icone}</Box>}
          <Typography variant="subtitle1" fontWeight={700} noWrap>{titulo}</Typography>
          {subtitulo && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', md: 'block' } }}>
              {subtitulo}
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
          {acoes}
          {totalPaginas > 1 && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: 'center' }}>
                {paginaAtual + 1}/{totalPaginas}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setPagina(paginaAtual - 1)}
                disabled={paginaAtual === 0}
                sx={botaoSx}
                aria-label="Anteriores"
              >
                <ChevronLeftRoundedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setPagina(paginaAtual + 1)}
                disabled={paginaAtual >= totalPaginas - 1}
                sx={botaoSx}
                aria-label="Próximos"
              >
                <ChevronRightRoundedIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      <Box
        ref={linhaRef}
        display="grid"
        gap={1.5}
        sx={{ gridTemplateColumns: `repeat(${porPagina}, minmax(0, 1fr))` }}
      >
        {visiveis}
      </Box>
    </Box>
  );
};
